/**
 * WP dependencies
 */

import apiFetch from '@wordpress/api-fetch';
import childrensCategories from '../../components/utils/childrens-categories';

import {
  RadioControl,
  SelectControl,
  TextControl,
  ToggleControl,
  TextareaControl
} from '@wordpress/components';

import { useSelect } from '@wordpress/data';
import { useEffect, useMemo, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * listInspectorControls
 *
 * A DWT reusable component
 *
 * @param {Object}  props
 * @param {Object}  props.setAttributes
 * @param {boolean} props.isCarousel
 * @param {boolean} props.isGrid
 * @param {boolean} props.isProductList
 * @param {string}  props.featureBy
 * @param {string}  props.filterBy
 * @param {string}  props.divisionCode
 * @param {string}  props.imprintCode
 * @param {string}  props.seriesCode
 * @param {string}  props.bisacCode
 * @param {string}  props.brandCode
 * @param {string}  props.themeCode
 * @param {string}  props.categoryCode
 * @param {string}  props.ageRangeCode
 * @param {string}  props.collectionISBNs
 * @param {boolean} props.showFormats
 * @param {boolean} props.showMetadata
 * @param {boolean} props.showCovers
 * @param {boolean} props.showBreadcrumb
 * @param {boolean} props.hideAwardWinners
 * @param {Array}   props.filterByOptions
 * @param {Array}   props.innerBlocks
 */
const listInspectorControls = ({
  setAttributes,
  isCarousel,
  isGrid,
  isProductList,
  featureBy,
  filterBy,
  divisionCode,
  imprintCode,
  seriesCode,
  bisacCode,
  categoryCode,
  ageRangeCode,
  brandCode,
  themeCode,
  collectionISBNs,
  showMetadata,
  showFormats,
  showChildrensCategories,
  selectedChildrensCategories,
  showLanguages,
  showCovers,
  hideAwardWinners,
  showBreadcrumb,
  selectedFormats,
  selectedLanguages,
  filterByOptions,
  innerBlocks
}) => {
  const { postType } = useSelect( ( select ) => {
    const { getCurrentPostType } = select( 'core/editor' );

    return {
      postType: getCurrentPostType()
    };
  }, [] );

  const isImprintPost = postType === 'imprint';
  const postImprintCode = acf.getFields({ name: 'imprint_code' })[0];
  const imprintCodeField = postImprintCode?.val();

  const isCategoryPost = postType === 'bookcategory';
  const postBisac = acf.getFields({ name: 'bisac' })[0];
  const bisacField = postBisac?.val();

  const isSeriesPost = postType === 'series';
  const postSeriesCode = acf.getFields({ name: 'series_code' })[0];
  const seriesCodeField = postSeriesCode?.val();

  const isAgeRangePost = postType === 'agerange';
  const postAgeRangeCode = acf.getFields({ name: 'age_range_code' })[0];
  const ageRangeCodeField = postAgeRangeCode?.val();

  const isBrandPost = postType === 'brand';
  const postBrandCode = acf.getFields({ name: 'brand_codes' })[0];
  const brandCodeField = postBrandCode?.val();

  const isThemePost = postType === 'booktheme';
  const postThemeCode = acf.getFields({ name: 'theme_codes' })[0];
  const themeCodeField = postThemeCode?.val();

  const isCollectionPost = postType === 'collection';
  const postCollectionISBNs = acf.getFields({ name: 'collection_isbns' })[0];
  const collectionISBNsField = postCollectionISBNs?.val();

  /**
   * Feature by settings
   */

  const handleFeatureBy = ( newFeatureBy ) => {
    setAttributes({ featureBy: newFeatureBy });
    innerBlocks.length = 0;
  };

  /**
   * Filters
   */

  const handleFilterBy = ( newFilterBy ) => {
    setAttributes({ filterBy: newFilterBy });
    setAttributes({ seriesCode: '' });

    if ( filterBy !== 'custom' ) {
      innerBlocks.length = 0;
    }
  };

  const handleDivisionCode = ( newDivisionCode ) =>
    setAttributes({ divisionCode: newDivisionCode });

  const handleImprintCode = ( newImprintCode ) => {
    if ( isImprintPost ) return;
    setAttributes({ imprintCode: newImprintCode });
  };

  const handleSeriesCode = ( newSeriesCode ) => 
    setAttributes({ seriesCode: newSeriesCode });


  const handleBrandCode = ( newBrandCode ) =>
    setAttributes({ brandCode: newBrandCode });

  const handleBisacCode = ( newBisacCode ) =>
    setAttributes({ bisacCode: newBisacCode });

  const handleSelectedFormats = ( newSelectedFormats ) =>
    setAttributes({ selectedFormats: newSelectedFormats });

  const handleSelectedChildrensCategories = ( newSelectedChildrensCategories ) =>
    setAttributes({ selectedChildrensCategories: newSelectedChildrensCategories });

  const handleSelectedLanguages = ( newSelectedLanguages ) =>
    setAttributes({ selectedLanguages: newSelectedLanguages });

  /**
   * Handle additional logic
   */

  const filterByHelpText =
        filterBy === 'custom'
          ? `Manually add up to 12 custom titles.`
          : `Automatic API lists are only displayed on the frontend. Click on Preview or View Page to see list.`;

  useEffect( () => {
    if ( !isImprintPost ) return;
    setAttributes({ imprintCode: imprintCodeField });
  }, [ imprintCodeField ] );

  useEffect( () => {
    if ( !isCategoryPost ) return;
    setAttributes({ categoryCode: bisacField });
  }, [ bisacField ] );

  useEffect( () => {
    if ( !isSeriesPost ) return;
    setAttributes({ seriesCode: seriesCodeField });
  }, [ seriesCodeField ] );

  useEffect( () => {
    if ( !isBrandPost ) return;
    setAttributes({ brandCode: brandCodeField });
  }, [ brandCodeField ] );

  useEffect( () => {
    if ( !isAgeRangePost ) return;
    setAttributes({ ageRangeCode: ageRangeCodeField });
  }, [ ageRangeCodeField ] );

  useEffect( () => {
    if ( !isBrandPost ) return;
    setAttributes({ brandCode: brandCodeField });
  }, [ brandCodeField ] );

  useEffect( () => {
    if ( !isThemePost ) return;
    setAttributes({ themeCode: themeCodeField });
  }, [ themeCodeField ] );

  useEffect( () => {
    if ( !isCollectionPost ) return;
    setAttributes({ collectionISBNs: collectionISBNsField });
  }, [ collectionISBNsField ] );

  // Set filterBy to empty string only on initial load if not already set
  useEffect( () => {
    // Only run this once on component mount, not on every postType change
    if ( filterBy === undefined || filterBy === null ) {
      setAttributes({ filterBy: '' });
    }
  }, [] );

  const [ formatData, setFormatData ] = useState( [] );

  useEffect( () => {
    if ( isSeriesPost ) return;
    ( async() => {
      await apiFetch({
        path: '/dwt/v2/list-format',
        method: 'POST',
        data: { rows: 0 }
      })
        .then( ( response ) => {
          return setFormatData( response.data.formats );
        })
        .catch( ( error ) => {
          console.error( error );
        });
    })();
  }, [] );

  useMemo( () => {
    if ( isSeriesPost ) return;
    return formatData.forEach( ( item ) => {
      item.value = item.code;
      item.label = item.code === 'DG' ? 'Digest' : item.desc;
      item.label += ` (${item.value.replace( /-/g, '' )})`;

      delete item._embeds;
      delete item._links;
      delete item.code;
      delete item.desc;
    });
  }, [ formatData ] );

  useEffect( () => {
    if ( isSeriesPost ) return;

    const consolidate = {
      CD: 'Compact Disc (CD)',
      DN: 'Audio Download (DN)',
      HC: 'Hardcover (HC)',
      NT: 'Other (NT)',
      TR: 'Trade Paperback (TR)'
    };

    formatData.forEach( ( item ) => {
      for ( const [ key, value ] of Object.entries( consolidate ) ) {
        if ( item.value.includes( key ) ) {
          item.label = value;
        }
      }
    });
  }, [ formatData ] );

  const uniqueFormatData = formatData.filter(
    ( obj, index ) =>
      index === formatData.findIndex( ( o ) => obj.value === o.value )
  );

  // Display Name and catId value
  const childrensCategoriesOptions = childrensCategories.map( category => ({
    label: category['be-label'],
    value: category.value
  }) );

  const showBisacControl =
    (
      ( isCarousel || isGrid ) &&
      featureBy === 'books' &&
      [ 'showBestsellers', 'showComingSoon', 'showNewReleases' ].includes( filterBy )
    ) ||
    ( isProductList && filterBy === 'bisac' );

  return (
    <>
      {!isCollectionPost && (
        <RadioControl
          label={__( 'Feature', 'dwt-custom-theme' )}
          help={__( 'Feature books or contributors', 'dwt-custom-theme' )}
          selected={featureBy}
          options={[
            { label: 'Books', value: 'books' },
            { label: 'Contributors', value: 'contributors' }
          ]}
          onChange={handleFeatureBy}
        />
      )}
      <SelectControl
        label={__( 'Filter by', 'dwt-custom-theme' )}
        help={__( filterByHelpText, 'dwt-custom-theme' )}
        value={filterBy}
        options={filterByOptions}
        onChange={handleFilterBy}
        disabled={!!isSeriesPost}
        __nextHasNoMarginBottom
        __next40pxDefaultSize
      />
      <ToggleControl
        className="editor-toggle-button"
        label={__( 'Metadata Options', 'dwt-custom-theme' )}
        help={__(
          showMetadata
            ? 'Metadata is displayed.'
            : 'Metadata is not displayed',
          'dwt-custom-theme'
        )}
        checked={showMetadata}
        onChange={() => {
          setAttributes({ showMetadata: !showMetadata });
        }}
        __nextHasNoMarginBottom
      />
      {filterBy !== 'custom' && (
        <ToggleControl
          className="editor-toggle-button"
          label={__(
            'Cover/Headshot Image Options',
            'dwt-custom-theme'
          )}
          help={__(
            showCovers
              ? 'Items with covers or headshots are displayed'
              : 'All items are displayed',
            'dwt-custom-theme'
          )}
          checked={showCovers}
          onChange={() => {
            setAttributes({ showCovers: !showCovers });
          }}
          __nextHasNoMarginBottom
        />
      )}
      {!isCollectionPost && filterBy === '' && (
        <ToggleControl
          className="editor-toggle-button"
          label={__(
            'Suppress Award Winners Filter',
            'dwt-custom-theme'
          )}
          help={__(
            hideAwardWinners
              ? 'The Award Winners filter is hidden.'
              : 'Toggle on to Hide the Award Winners.',
            'dwt-custom-theme'
          )}
          checked={hideAwardWinners}
          onChange={() => {
            setAttributes({ hideAwardWinners: !hideAwardWinners });
          }}
          __nextHasNoMarginBottom
        />
      )}
      {!isSeriesPost && !isCollectionPost && !isCarousel && !isGrid && (
        <ToggleControl
          className="editor-toggle-button"
          label={__( 'Format Filter', 'dwt-custom-theme' )}
          help={__(
            showFormats
              ? 'Select one or more formats to filter by. Non-traditional will display as Other.'
              : 'Add a filter for book formats.',
            'dwt-custom-theme'
          )}
          checked={showFormats}
          onChange={() => {
            setAttributes({
              showFormats: !showFormats,
              selectedFormats: []
            });
          }}
          __nextHasNoMarginBottom
        />
      )}
      {showFormats && !isSeriesPost && !isCollectionPost && (
        <SelectControl
          multiple
          label={__( 'Select format(s):' )}
          value={selectedFormats}
          options={uniqueFormatData}
          onChange={handleSelectedFormats}
          help={__(
            'Hold command/control to select multiple formats.'
          )}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}
      {!isSeriesPost && !isCollectionPost && !isCarousel && !isGrid && (
        <ToggleControl
          className="editor-toggle-button"
          label={__( 'Children\'s Category Filter', 'dwt-custom-theme' )}
          help={__(
            showChildrensCategories
              ? 'Select one or more categories to filter by.'
              : 'Add a filter for children\'s categories.',
            'dwt-custom-theme'
          )}
          checked={showChildrensCategories}
          onChange={() => {
            setAttributes({
              showChildrensCategories: !showChildrensCategories,
              selectedChildrensCategories: []
            });
          }}
          __nextHasNoMarginBottom
        />
      )}
      {showChildrensCategories && (
        <SelectControl
          multiple
          label={__( 'Select categories:' )}
          value={selectedChildrensCategories}
          options={childrensCategoriesOptions}
          onChange={handleSelectedChildrensCategories}
          help={__(
            'Hold command/control to select multiple categories.'
          )}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}
      {!isSeriesPost && !isCollectionPost && !isCarousel && !isGrid && (
        <ToggleControl
          className="editor-toggle-button"
          label={__( 'Language Filter', 'dwt-custom-theme' )}
          help={__(
            showLanguages
              ? 'Select one or more languages to add as a filter.'
              : 'Add a filter for language.',
            'dwt-custom-theme'
          )}
          checked={showLanguages}
          onChange={() => {
            setAttributes({
              showLanguages: !showLanguages,
              selectedLanguages: []
            });
          }}
          __nextHasNoMarginBottom
        />
      )}
      {showLanguages && !isSeriesPost && !isCollectionPost && (
        <SelectControl
          multiple
          label={__( 'Select language(s):' )}
          value={selectedLanguages}
          options={[
            { label: 'English', value: 'E' },
            { label: 'Spanish', value: 'SP' }
          ]}
          onChange={handleSelectedLanguages}
          help={__(
            'Hold command/control to select multiple languages.'
          )}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}
      {filterBy !== 'custom' && !isSeriesPost && !isCollectionPost && (
        <>
          <h4>Codes</h4>
          <p className="mb-24">
                        Add one or more of each. For more than one code, use a
                        comma to separate them.
          </p>
          <TextControl
            label={__( 'Division', 'dwt-custom-theme' )}
            help={__( 'Add division code(s).', 'dwt-custom-theme' )}
            value={divisionCode}
            onChange={handleDivisionCode}
            __nextHasNoMarginBottom
            __next40pxDefaultSize
          />
          <TextControl
            label={__( 'Imprint', 'dwt-custom-theme' )}
            help={__(
              `${
                !isImprintPost
                  ? 'Add imprint code(s).'
                  : 'Imprint code(s) are set at the post level'
              }`,
              'dwt-custom-theme'
            )}
            value={imprintCode}
            onChange={handleImprintCode}
            readOnly={isImprintPost ? true : null}
            __nextHasNoMarginBottom
            __next40pxDefaultSize
          />
          {isCategoryPost && (
            <TextControl
              label={__( 'Category', 'dwt-custom-theme' )}
              help={__(
                'BISAC code(s) are set at the post level',
                'dwt-custom-theme'
              )}
              value={categoryCode}
              readOnly
              __nextHasNoMarginBottom
              __next40pxDefaultSize
            />
          )}
          {isAgeRangePost && (
            <TextControl
              label={__( 'Age Range', 'dwt-custom-theme' )}
              help={__(
                'Age Range code(s) are set at the post level',
                'dwt-custom-theme'
              )}
              value={ageRangeCode}
              readOnly
              __nextHasNoMarginBottom
              __next40pxDefaultSize
            />
          )}
          {isBrandPost && (
            <TextControl
              label={__( 'Brand', 'dwt-custom-theme' )}
              help={__(
                'Brand code(s) are set at the post level',
                'dwt-custom-theme'
              )}
              value={brandCode}
              readOnly
              __nextHasNoMarginBottom
              __next40pxDefaultSize
            />
          )}
          {isThemePost && (
            <TextControl
              label={__( 'Theme', 'dwt-custom-theme' )}
              help={__(
                'Theme code(s) are set at the post level',
                'dwt-custom-theme'
              )}
              value={themeCode}
              readOnly
              __nextHasNoMarginBottom
              __next40pxDefaultSize
            />
          )}
        </>
      )}
      {isCollectionPost && (
        <TextareaControl
          label={__( 'Collection', 'dwt-custom-theme' )}
          help={__(
            'Collection code(s) are set at the post level',
            'dwt-custom-theme'
          )}
          value={collectionISBNs}
          readOnly
        />
      )}
      {filterBy === 'series' && (
        <TextControl
          label={__( 'Series', 'dwt-custom-theme' )}
          help={__( 'Add series code(s).', 'dwt-custom-theme' )}
          value={seriesCode}
          onChange={handleSeriesCode}
          readOnly={!!isSeriesPost}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}
      {filterBy === 'brand' && (
        <TextControl
          label={__( 'Brand', 'dwt-custom-theme' )}
          help={__( 'Add Brand code(s).', 'dwt-custom-theme' )}
          value={brandCode}
          onChange={handleBrandCode}
          readOnly={!!isBrandPost}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}
      {showBisacControl && (
        <TextControl
          label={__( 'BISAC', 'dwt-custom-theme' )}
          help={__( 'Add BISAC code(s).', 'dwt-custom-theme' )}
          value={bisacCode}
          onChange={handleBisacCode}
          __nextHasNoMarginBottom
          __next40pxDefaultSize
        />
      )}

      { isProductList && (
        <>
          <hr className='my-24' />
          <h4>Settings</h4>
          <ToggleControl
            className='editor-toggle-button'
            label={ __( 'Show Breadcrumbs', 'dwt-custom-theme' ) }
            help={__( 'Show the breadcrumbs at the top of the Product List', 'dwt-custom-theme' ) }
            checked={ showBreadcrumb }
            onChange={ () => {
              setAttributes({ showBreadcrumb: !showBreadcrumb });
            } }
          />
        </>
      )}
    </>
  );
};

export default listInspectorControls;
